import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { acomptesAPI } from '../../../services/api';
import { Acompte, AcompteValidate, StatutAcompte } from '../../../types/acompte';
import { extractErrorMessage } from '../../../utils/errorHandler';
import useAlert from '../../../hooks/useAlert';

const HR_COLOR = '#00897B';

interface AcompteValidationDialogProps {
  open: boolean;
  onClose: () => void;
  acompte: Acompte | null;
  onSuccess: () => void;
}

const AcompteValidationDialog: React.FC<AcompteValidationDialogProps> = ({
  open,
  onClose,
  acompte,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [commentaire, setCommentaire] = useState('');
  const [datePaiement, setDatePaiement] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { showSuccess, showError } = useAlert();

  useEffect(() => {
    if (open && acompte) {
      setCommentaire('');
      // Default to end of current month for payment date
      const now = new Date();
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setDatePaiement(lastDayOfMonth.toISOString().split('T')[0]);
      setError(null);
    }
  }, [open, acompte]);

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount) + ' DH';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const handleValidate = async (statut: StatutAcompte) => {
    if (statut === 'VALIDEE' && !datePaiement) {
      setError('Veuillez specifier la date de paiement (deduction) pour valider la demande');
      return;
    }

    setLoading(true);
    try {
      const data: AcompteValidate = {
        statut,
        commentaire: commentaire || null,
        date_paiement: statut === 'VALIDEE' ? datePaiement : null,
      };
      await acomptesAPI.validate(acompte!.id, data);
      showSuccess(statut === 'VALIDEE' ? 'Demande validee avec succes' : 'Demande refusee');
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!acompte) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: HR_COLOR, color: 'white' }}>
        Validation de la Demande d'Acompte
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Request Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Details de la demande
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Reference:</Typography>
              <Typography variant="body1" fontWeight="500">{acompte.ref_acompte || '-'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Date de demande:</Typography>
              <Typography variant="body1">{formatDate(acompte.date_demande)}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Employee Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Employe
          </Typography>
          <Typography variant="body1" fontWeight="500">
            {acompte.employee?.nom_complet}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {acompte.employee?.matricule} - {acompte.employee?.fonction || 'N/A'}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Financial Details */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">Montant demande:</Typography>
          <Typography variant="h5" sx={{ color: HR_COLOR }}>
            {formatMoney(acompte.montant)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Ce montant sera deduit du prochain salaire
          </Typography>
        </Box>

        {acompte.motif && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Motif
            </Typography>
            <Typography variant="body2">{acompte.motif}</Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Validation Form */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Date de Paiement (Deduction)"
              type="date"
              value={datePaiement}
              onChange={(e) => {
                setDatePaiement(e.target.value);
                setError(null);
              }}
              InputLabelProps={{ shrink: true }}
              helperText="Date a laquelle l'acompte sera deduit du salaire"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Commentaire"
              multiline
              rows={2}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              placeholder="Ajouter un commentaire (optionnel)..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => handleValidate('REFUSEE')}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Refuser'}
        </Button>
        <Button
          variant="contained"
          onClick={() => handleValidate('VALIDEE')}
          disabled={loading}
          sx={{ bgcolor: '#66BB6A', '&:hover': { bgcolor: '#4CAF50' } }}
        >
          {loading ? <CircularProgress size={24} /> : 'Valider'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AcompteValidationDialog;
